"use client";

import { startTransition, useEffect, useMemo, useRef, useState } from "react";
import { useLocalizedRouter } from "@/hooks/useLocalizedRouter";
import { useAuth } from "@/contexts/AuthContext";
import { LoginDialog } from "@/components/shared/LoginDialog";
import type { LeadContext } from "@/lib/leadClient";
import { createLeadCreateEvent } from "@/lib/leadClient";
import {
  getStoredUtmChannelAttribution,
  lastTouchUtmParams,
} from "@/lib/utmChannelCookies";
import { stashFindWorkshopServiceId } from "@/lib/findWorkshopSession";
import { resolveSelectedNavCityClient } from "@/lib/navCityCookie";
import { GTMEvents, PushDataToGTM } from "@/lib/gtm";
import { splitPhoneByCountryCode } from "@/app/(ar)/contact/ContactPageClient";

/** GTM label segment: trailing `Form` → `SUBMIT` (e.g. SERVICELEADFORM → SERVICELEADSUBMIT). */
function eventCategoryForSubmitLabel(eventCategory: string): string {
  const cat = eventCategory.trim();
  return /form$/i.test(cat) ? cat.replace(/form$/i, "SUBMIT") : cat;
}

export function FindWorkshopButtonClient({
  label,
  leadContext,
  serviceIdToStash,
  className,
  pageName,
  pageCategory,
  eventCategory,
}: {
  label: string;
  leadContext: LeadContext;
  /** If provided, stashes the service id so /find-workshop can pre-filter. */
  serviceIdToStash?: string;
  className: string;
  pageName: string;
  pageCategory: string;
  eventCategory: string;
}) {
  const { push } = useLocalizedRouter();
  const { isLoggedIn, isReady, user } = useAuth();
  const [loginOpen, setLoginOpen] = useState(false);
  const pendingRef = useRef(false);
  const savedCity = resolveSelectedNavCityClient();

  const go = useMemo(() => {
    return () => {
      if (serviceIdToStash) stashFindWorkshopServiceId(serviceIdToStash);
      push("/find-workshop");
    };
  }, [push, serviceIdToStash]);

  const handleClick = async () => {
    PushDataToGTM(GTMEvents.DataLayerUpdate, {
      pageName: pageName,
      pageCategory: pageCategory,
      eventCategory: eventCategory,
      eventLabel: pageName + " > FIND WORKSHOP",
      stage: 1,
    });
    if (serviceIdToStash) stashFindWorkshopServiceId(serviceIdToStash);

    if (!isReady) {
      pendingRef.current = true;
      return;
    }

    if (isLoggedIn) {
      if (user?.customerId) {
        const { phoneCode, phoneNumber } = splitPhoneByCountryCode(user.phone);
        const { last } = getStoredUtmChannelAttribution();
        const { utm_id, ...utmParams } = lastTouchUtmParams(last);
        void utm_id;
        await createLeadCreateEvent({
          context: leadContext,
          verified: 1,
          user,
          customerId: user.customerId ?? "NA",
          email: user.email ?? "NA",
          customerName: user.name ?? "NA",
          phoneCode,
          phoneNumber,
          utmParams,
          consent: true,
          accept: true,
          cityId: savedCity?.id?.toString() ?? "",
          city: savedCity?.nameEn ?? "",
        });
        PushDataToGTM(GTMEvents.DataLayerUpdate, {
          pageName: pageName,
          pageCategory: pageCategory,
          eventCategory: eventCategory,
          eventLabel: pageName + " > PREAUTHORISED",
          stage: 2,
        });
        PushDataToGTM(GTMEvents.DataLayerUpdate, {
          pageName: pageName,
          pageCategory: pageCategory,
          eventCategory: eventCategory,
          eventLabel: `${pageName} > ${eventCategoryForSubmitLabel(eventCategory)}`,
          stage: 8,
        });
      }
      go();
      return;
    }
    setLoginOpen(true);
  };

  useEffect(() => {
    if (!pendingRef.current) return;
    if (!isReady) return;
    pendingRef.current = false;
    if (isLoggedIn) {
      void (async () => {
        if (user?.customerId) {
          const { phoneCode, phoneNumber } = splitPhoneByCountryCode(user.phone || "");
          const { last } = getStoredUtmChannelAttribution();
          const { utm_id, ...utmParams } = lastTouchUtmParams(last);
          void utm_id;
          await createLeadCreateEvent({
            context: leadContext,
            verified: 1,
            user,
            customerId: user.customerId ?? "NA",
            email: user.email ?? "NA",
            customerName: user.name ?? "NA",
            phoneCode,
            phoneNumber,
            utmParams,
            consent: true,
            accept: true,
            cityId: savedCity?.id?.toString() ?? "",
            city: savedCity?.nameEn ?? "",
          });
        }
        PushDataToGTM(GTMEvents.DataLayerUpdate, {
          pageName: pageName,
          pageCategory: pageCategory,
          eventCategory: eventCategory,
          eventLabel: `${pageName} > ${eventCategoryForSubmitLabel(eventCategory)}`,
          stage: 8,
        });
        go();
      })();
    } else {
      startTransition(() => {
        setLoginOpen(true);
      });
    }
  }, [isReady, isLoggedIn, user, leadContext, go]);

  return (
    <>
      <button type="button" onClick={handleClick} className={className}>
        {label}
      </button>
      <LoginDialog
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        returnUrl="/find-workshop"
        onLoggedIn={() => {
          PushDataToGTM(GTMEvents.DataLayerUpdate, {
            pageName: pageName,
            pageCategory: pageCategory,
            eventCategory: eventCategory,
            eventLabel: `${pageName} > ${eventCategoryForSubmitLabel(eventCategory)}`,
            stage: 8,
          });
          if (serviceIdToStash) stashFindWorkshopServiceId(serviceIdToStash);
        }}
        leadContext={leadContext}
        pageName={pageName}
        pageCategory={pageCategory}
        eventCategory={eventCategory}
      />
    </>
  );
}

